import { PayloadAction } from '@reduxjs/toolkit';
import {
  AuthActions,
  AuthSelectors,
  FirebaseActions,
  FirebaseSelectors,
  NotificationActions,
  SentryService,
} from '@web_opxp/features';
import { LanguageActions, LanguageSelectors, LanguageService } from '@web_opxp/language';
import { all, call, put, select, take, takeEvery, takeLeading } from 'redux-saga/effects';
import { ApiService, HistoryService, HttpError } from 'web_core_library';
import { detectLandingPageTracking } from '../base/cookieService';
import ENV from '../base/env';
import { getCountryCodeFromUrl, getProductIdFromUrl } from '../base/utils';
import { CouponActions, CouponSelectors } from '../coupon';
import testForApplePayment from './applePayService';
import ChargebeeService from './chargebeeService';
import CheckoutService from './checkoutService';
import * as Selectors from './selectors';
import { isCheckoutReady } from './selectors';
import { CheckoutActions } from './slice';
import { IHostedPageData, IHostedPageResponse } from './types';

export default function* checkoutWatcher() {
  yield all([
    takeEvery(CheckoutActions.initCheckout, initCheckoutSaga),
    takeLeading(CheckoutActions.startCheckout, startCheckoutSaga),
  ]);
}

export function* initCheckoutSaga() {
  // init i18n feature
  yield put(LanguageActions.initLanguage());
  // init firebase
  const searchParams: URLSearchParams = yield call(HistoryService.getCurrentSearchParams);
  yield put(
    FirebaseActions.firebaseInit(
      ENV.FirebaseConfig.apiKey,
      ENV.FirebaseConfig.projectId,
      ENV.FirebaseConfig.appId,
      ENV.FirebaseConfig.messagingSenderId,
      ENV.FirebaseConfig.measurementId,
      searchParams
    )
  );
  yield take(FirebaseActions.firebaseInitialized.type);
  yield call(detectLandingPageTracking);

  yield put(FirebaseActions.firebaseStart({ analytics: true, config: true, messaging: false }));
  // Init ApiService
  yield call(ApiService.init, ENV.ApiUrl);

  // start session token restoration
  yield put(
    AuthActions.authRestore({
      getCurrentSearchParams: HistoryService.getCurrentSearchParams,
      exchangeAccessToken: HistoryService.exchangeAccessToken,
      updateSearchParams: HistoryService.updateSearchParams,
      refreshToken: HistoryService.refreshToken,
      redirectLogin: HistoryService.redirectLogin,
      redirectLogout: HistoryService.redirectLogout,
    })
  );
  const authResult = yield take([AuthActions.restoreAuthSuccess, AuthActions.restoreAuthFail]);
  if (authResult.type === AuthActions.restoreAuthSuccess.type) {
    const userId = yield select(AuthSelectors.getUserId);
    yield call(SentryService.setUserId, userId);
    // set user id for tracking
    yield put(FirebaseActions.setUser(userId));
  }
  // initialize coupon feature
  yield put(CouponActions.couponInit());

  yield call(CheckoutService.init, ApiService);
  yield put(CheckoutActions.checkoutReady());
}

export function* waitForReady() {
  const isReady = yield select(isCheckoutReady);
  if (!isReady) {
    yield take(CheckoutActions.checkoutReady);
  }
}

export function* waitForLanguage() {
  let languageIsLoading = yield select(LanguageSelectors.isLoading);
  while (languageIsLoading) {
    yield take(LanguageActions.updateLanguage);
    languageIsLoading = yield select(LanguageSelectors.isLoading);
  }
}

export function* waitForCoupon() {
  const isCouponLoaded = yield select(CouponSelectors.isLoaded);
  if (!isCouponLoaded) {
    yield take(CouponActions.couponUpdate);
  }
}

export function* ensureAuthorizedUser() {
  const isAppLoading = yield !select(Selectors.isCheckoutReady);
  if (isAppLoading) {
    // wait until app is loaded
    yield take(CheckoutActions.checkoutReady);
  }
  const isUserAuthorized = yield select(AuthSelectors.isAuthorized);
  const userVerified = yield select(AuthSelectors.isVerified);
  if (!isUserAuthorized || !userVerified) {
    // if authorization failed - redirect to login
    const errorText = LanguageService.getTFunction()('common.common:Errors.SessionExpired');
    yield put(NotificationActions.showError(errorText));
    yield put(AuthActions.redirectToSSO());
  }
}

export function* waitForGDPR() {
  const consentGiven = yield select(FirebaseSelectors.isConsentGiven);
  if (!consentGiven) {
    yield take(FirebaseActions.updateConsent.type);
  }
}

export function* testForApplePay(url: string) {
  const validBrowser = yield call(testForApplePayment);
  if (!validBrowser) {
    console.warn('Browser does not support Apple Pay!');
    return false;
  }
  console.warn('Browser supports Apple Pay. Trying to open proper hosted page in a separate tab.');
  yield call(HistoryService.openExternalUrl, url);
  return true;
}

export function* startCheckoutSaga(action: PayloadAction<string>) {
  const planId = action.payload;
  yield call(waitForLanguage);
  yield call(ensureAuthorizedUser);
  let hostedPageData: IHostedPageData;
  try {
    yield call(waitForReady);
    yield call(waitForCoupon);
    yield call(waitForGDPR);
    const trackingEnabled: boolean = yield select(FirebaseSelectors.getFirebaseConsent);
    yield call(ChargebeeService.init, trackingEnabled);
    const productId: string | undefined = yield call(getProductIdFromUrl);
    if (!productId) {
      throw new Error('Product id must be specified!');
    }
    const country: string | undefined = yield call(getCountryCodeFromUrl);
    if (!country) {
      throw new Error('Country code must be specified!');
    }
    const locale: string = yield select(LanguageSelectors.getLanguage);
    const coupon: string = yield select(CouponSelectors.getCurrentCoupon);
    const email: string = yield select(AuthSelectors.getUserEmail);
    const response: IHostedPageResponse = yield call(
      CheckoutService.getCheckoutUrl,
      planId,
      productId,
      locale,
      country,
      coupon,
      email
    );
    hostedPageData = response.data;
  } catch (error) {
    if ((error as HttpError).status === 422) {
      yield put(
        NotificationActions.showError(`Error while fetching checkout url: ${(error as HttpError).data.message}`)
      );
      return;
    }
    yield call(console.error, error);
    yield put(NotificationActions.showError(`Error while fetching checkout url: ${(error as Error).message}`));
    return;
  }
  yield put(CheckoutActions.hostedPageLoaded(hostedPageData));
}
